import React, { useEffect, useState } from "react";
import Card from "../../shared/widgets/Card/Card";
import CardLayout from "../../shared/pageLayouts/cardLayout/CardLayout";
import { APIService } from "../../../services/APIService";
import { ServiceURI } from "../../../services/ServiceURI";
import CreateLayout from "../../shared/pageLayouts/createLayout/CreateLayout";
import FormSidebar from "../../shared/widgets/formSidebar/FormSidebar";
import { AgentFormSidebarMenu } from "./AgentForm";
import "./Agents.scss";
import { Button } from "primereact/button";
import SharedFlow from "../../shared/widgets/ReactFlow/ReactFlow";
import { INode } from "../../shared/widgets/ReactFlow/models/node.model";
import { IEdge } from "../../shared/widgets/ReactFlow/models/edge.model";
import ListSelect from "../../shared/widgets/Form/listSelect/ListSelect";
import SharedDropdown from "../../shared/widgets/Form/dropdown/Dropdown";
import { TypeEnum } from "../../shared/widgets/Form/enums/TypeEnum";
import SharedInputTextArea from "../../shared/widgets/Form/inputTextArea/inputTextArea";
import { IAgent } from "../../../models/Agent.model";
import { ITool } from "../../../models/Tool.model";
import { NodeTypesEnum } from "../../shared/widgets/ReactFlow/enum/Nodeflow.enum";
import { Utils } from "../../../utils/Utils";
import SharedInputText from "../../shared/widgets/Form/inputText/InputText";
import { FaUserAlt } from "react-icons/fa";
import AutoResizeTextarea from "../../shared/widgets/Form/AutoResizeTextarea/AutoResizeTextarea";
import { ReactFlowProvider } from "@xyflow/react";

const initialAgentForm = {
  agentName: "",
  agentDescription: "",
  prompt: "",
  tools: [""],
  model: "",
  flow: { nodes: [], edges: [] },
  welcomeText: "",
};

export default function Agents() {
  const [agents, setAgents] = useState<IAgent[]>([]);
  const [activeTabName, setActiveTabName] = useState("profile");
  const [isCreateTab, setIsCreateTab] = useState(false);
  const [agentForm, setAgentForm] = useState(initialAgentForm);
  const [tools, setTools] = useState<ITool[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);

  const fetchTools = async () => {
    const res = await APIService.getCall(ServiceURI.TOOLS.FATCH_TOOLS);
    if (res) {
      setTools(res);
    }
  };

  const fetchAgents = async () => {
    const res = await APIService.getCall(ServiceURI.AGENTS.FATCH_AGENTS);
    if (res) {
      setAgents(res);
    }
  };

  useEffect(() => {
    fetchAgents();
    fetchTools();
  }, []);

  const handleNextActiveName = () => {
    AgentFormSidebarMenu.forEach((item, i) => {
      item.menus.forEach((subItem, j) => {
        if (subItem.tab === activeTabName) {
          if (j === item.menus.length - 1) {
            setActiveTabName(AgentFormSidebarMenu[i + 1].menus[0].tab);
          } else {
            setActiveTabName(AgentFormSidebarMenu[i].menus[j + 1].tab);
          }
        }
      });
    });
  };

  const handleCardClick = (index: number) => {
    if (agents[index].agentFormData) {
      setIsEditMode(true);
      setActiveTabName("profile");
      setIsCreateTab(true);
      setAgentForm(agents[index].agentFormData);
    }
  };

  return (
    <ReactFlowProvider>
      <div className="agent-layout">
        {isCreateTab ? (
          <CreateLayout
            title="Agent"
            sidebarChildren={
              <FormSidebar
                activeTabName={activeTabName}
                setActiveTabName={setActiveTabName}
                sidebarList={AgentFormSidebarMenu}
              />
            }
            contentChildren={AgentContent(
              activeTabName,
              handleNextActiveName,
              setIsCreateTab,
              agentForm,
              setAgentForm,
              tools,
              isEditMode
            )}
            goBack={() => setIsCreateTab(false)}
          />
        ) : (
          <CardLayout
            title="Agents"
            addButtonClicked={() => {
              setIsEditMode(false);
              setAgentForm(initialAgentForm);
              setIsCreateTab(true);
            }}
          >
            {agents.map((item, i) => {
              return (
                <Card
                  key={i}
                  icon={<FaUserAlt />}
                  item={{
                    title: item.name,
                    content: item.description,
                    logo: item.logo,
                  }}
                  subTitle={
                    <p className="sub-title">
                      <span>Category</span> : {item.category}
                    </p>
                  }
                  handleCardClick={() => handleCardClick(i)}
                />
              );
            })}
          </CardLayout>
        )}
      </div>
    </ReactFlowProvider>
  );
}

const AgentContent = (
  activeName: string,
  handleNextActiveName: any,
  setIsCreateTab: any,
  agentForm: { [key: string]: any },
  setAgentForm: any,
  tools: ITool[],
  isEditMode: boolean
) => {
  const handleFormDataChange = (value: any, field: string) => {
    const agentFormData = Utils.deepClone(agentForm);
    agentFormData[field] = value;
    setAgentForm(agentFormData);
    if (field === "tools") {
      const selectedTools = tools.filter((toolItem) =>
        value.includes(toolItem.name)
      );
      const renderedWorkFlow: {
        nodes: INode[];
        edges: IEdge[];
      } = {
        nodes: selectedTools.map((toolItem, i) => {
          return {
            id: i.toString(),
            type: NodeTypesEnum.AgentNode,
            position: { x: (i + 1) * 300, y: 250 },
            data: {
              id: i.toString(),
              title: toolItem.name,
              functions: toolItem.functions.map((func) => func.function),
            },
            className: "",
          };
        }),

        edges: selectedTools.map((_, i) => {
          return {
            id: `e${i}-${i + 1}`,
            source: i.toString(),
            target: (i + 1).toString(),
            type: "smoothstep",
          };
        }),
      };
      setAgentForm((prev: any) => Object({ ...prev, flow: renderedWorkFlow }));
    }
  };
  let formRender;
  let footerRender;
  switch (activeName) {
    case "profile":
      formRender = (
        <div className="create-form-content">
          <h3>Agent Profile</h3>
          <div className="form">
            <SharedInputText
              item={{
                label: "Agent Name",
                field: "agentName",
                value: agentForm["agentName"],
                type: TypeEnum.Input,
              }}
              handleFormData={(v: string) =>
                handleFormDataChange(v, "agentName")
              }
            />
            <SharedInputTextArea
              item={{
                label: "Agent Description",
                field: "agentDescription",
                value: agentForm["agentDescription"],
                type: TypeEnum.Description,
                isRequired: true,
              }}
              handleFormData={(v: any) =>
                handleFormDataChange(v, "agentDescription")
              }
            />
          </div>
        </div>
      );
      break;
    case "core":
      formRender = (
        <div className="create-form-content">
          <h3>Core instructions</h3>
          <p>
            Describe how your agent should work. It's recommended to provide
            examples of tasks it might receive and what to do.
          </p>
          <div className="form">
            <AutoResizeTextarea
              value={agentForm["prompt"]}
              placeholder={`e.g. You are a helpful, respectful and honest assistant. If you don't know the answer to a question,
        please don't share false information`}
              onChange={(e: any) =>
                handleFormDataChange(e.target.value, "prompt")
              }
              rows={5}
              cols={30}
            />
          </div>
        </div>
      );
      break;
    case "tools":
      const toolListItems = tools.map((item, i) => {
        return {
          id: i.toString(),
          name: item.name,
          description: item.description,
          isSelected: agentForm["tools"].includes(item.name),
        };
      });
      formRender = (
        <div className="create-form-content">
          <h3>Add Tools</h3>
          <p>Add tools to support your agent</p>
          <ListSelect
            listItems={toolListItems}
            handleFormData={(v: any) => handleFormDataChange(v, "tools")}
            buttonLabel="TOOL"
          />
        </div>
      );
      break;
    case "integration":
      const integratinoListItems = [
        {
          id: "1",
          name: "Google (Gmail, Calendar & API)",
          description:
            "allows agents to automate communication, manage schedules, and streamline data access, enhancing productivity and simplifying workflows.",
        },
        {
          id: "2",
          name: "Microsoft & Outlook",
          description:
            "allows agents to automate communication, manage schedules, and streamline data access, enhancing productivity and simplifying workflows.",
        },
        {
          id: "3",
          name: "Slack",
          description:
            "allows agents to automate communication, enhancing productivity and simplifying workflows.",
        },
      ];
      formRender = (
        <div className="create-form-content">
          <h3>Connect your integrations</h3>
          <p>Add Integrations to automate your agent</p>
          <ListSelect
            listItems={integratinoListItems}
            buttonLabel="INTEGRATION"
          />
        </div>
      );
      break;
    case "settings":
      formRender = (
        <div className="create-form-content">
          <h3>Advanced settings</h3>
          <p>Customise your agents capabilities</p>

          <div className="create-settings-form">
            <div className="form1">
              <div className="form-group">
                <h4>Language model</h4>
                <p>
                  Select the LLM that powers your agent's reasoning, decision
                  making and text generation.
                </p>
              </div>
              <div className="form">
                <SharedDropdown
                  item={{
                    label: "",
                    field: "model",
                    value: agentForm["model"],
                    type: TypeEnum.Dropdown,
                    options: [
                      {
                        label: "gpt-4",
                        field: "gpt-4",
                      },
                      {
                        label: "gpt-4o",
                        field: "gpt-4o",
                      },
                    ],
                  }}
                  handleFormData={(v: any) => handleFormDataChange(v, "model")}
                />
              </div>
            </div>

            <div className="form-group">
              <h4>Welcome message</h4>
              <p>
                Set up a message that the agent will always send at the
                beginning of a task. Your agent will see this in its context.
              </p>
              <SharedInputTextArea
                item={{
                  label: "",
                  field: "welcomeText",
                  value: agentForm["welcomeText"],
                  type: TypeEnum.Description,
                }}
                handleFormData={(v: any) =>
                  handleFormDataChange(v, "welcomeText")
                }
              />
            </div>
          </div>
        </div>
      );
      break;
    case "builder":
      formRender = (
        <div className="create-form-content">
          <div style={{ flexGrow: 1 }}>
            <SharedFlow
              nodeProps={agentForm["flow"]}
              maxZoom={1}
              handleChangedNodes={(v: any) => handleFormDataChange(v, "flow")}
            />
          </div>
        </div>
      );
      break;
    default:
      formRender = <div>{activeName}</div>;
  }

  if (activeName !== "settings") {
    footerRender = (
      <div className="create-form-footer">
        <Button
          label="Cancel"
          severity="secondary"
          onClick={() => setIsCreateTab(false)}
          text
        />
        <Button
          label="Next"
          onClick={() => handleNextActiveName()}
          raised
          className="primary"
        />
      </div>
    );
  } else {
    footerRender = (
      <div className="create-form-footer">
        <Button
          label="Cancel"
          onClick={() => setIsCreateTab(false)}
          severity="secondary"
          text
        />
        <Button
          label={`${isEditMode ? "Update" : "Create"} Agent`}
          onClick={() => setIsCreateTab()}
          raised
          className="primary"
        />
      </div>
    );
  }

  return (
    <>
      {formRender}
      {footerRender}
    </>
  );
};
